import React, { useState } from 'react';
import { companyLogos } from '../data/mock';
import { ArrowLeft, ChevronDown, ChevronUp, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const teamMembers = [
  {
    id: 'rod-white',
    name: 'A. Rod White',
    title: 'Founder | CEO | Chief Systems Architect/Engineer',
    image: companyLogos.ceoHeadshot,
    bio: `Transformative AI is led by CEO & Chief Systems Architect/Engineer A. Rod White.

Before founding Transformative AI, Rod spent over two decades leading and architecting complex systems in environments where performance, compliance, and decision-making directly determined outcomes—including enterprise operations and high-level baseball management. His experience translating real-time performance systems into repeatable success now informs how Transformative AI designs durable, outcome-driven AI operating systems.`
  },
  {
    id: 'placeholder-1',
    name: 'Coming Soon',
    title: 'Position Open',
    image: null,
    bio: 'This position is currently open. We are always looking for talented individuals to join our team.'
  },
  {
    id: 'placeholder-2',
    name: 'Coming Soon',
    title: 'Position Open',
    image: null,
    bio: 'This position is currently open. We are always looking for talented individuals to join our team.'
  },
  {
    id: 'placeholder-3',
    name: 'Coming Soon',
    title: 'Position Open',
    image: null,
    bio: 'This position is currently open. We are always looking for talented individuals to join our team.'
  },
];

const TeamMemberCard = ({ member }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isPlaceholder = !member.image;

  return (
    <div className="flex flex-col">
      {/* Headshot - reduced to quarter size */}
      <div className="relative w-32 h-32 mx-auto mb-4">
        {!isPlaceholder && (
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 blur-lg" />
        )}
        {member.image ? (
          <img
            src={member.image}
            alt={member.name}
            className="relative w-full h-full object-cover object-top rounded-full border-2 border-white/10 shadow-xl"
          />
        ) : (
          <div className="relative w-full h-full rounded-full border-2 border-dashed border-white/20 bg-white/[0.02] flex items-center justify-center">
            <User className="w-12 h-12 text-white/20" />
          </div>
        )}
      </div>

      {/* Name and Title */}
      <div className="text-center mb-3">
        <h3 className={`font-display text-base ${isPlaceholder ? 'text-white/40' : 'text-white'} mb-1`}>
          {member.name}
        </h3>
        <p className="text-white/40 text-xs leading-tight">{member.title}</p>
      </div>

      {/* Dropdown Tab */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center justify-center gap-2 py-2 px-3 text-xs transition-colors border rounded-lg ${
          isPlaceholder 
            ? 'text-white/30 border-white/5 bg-white/[0.01] cursor-default'
            : 'text-purple-400 hover:text-purple-300 border-white/10 hover:border-purple-500/30 bg-white/[0.02]'
        }`}
        disabled={isPlaceholder}
      >
        {isExpanded ? 'Hide Bio' : 'View Bio'}
        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* Expandable Bio */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-96 mt-3' : 'max-h-0'
        }`}
      >
        <div className="p-3 bg-white/[0.02] border border-white/10 rounded-lg">
          <p className="text-white/60 text-xs leading-relaxed whitespace-pre-line">
            {member.bio}
          </p>
        </div>
      </div>
    </div>
  );
};

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050508]/95 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <nav className="flex items-center justify-between h-20">
            <a href="/" className="relative z-10">
              <img
                src={companyLogos.mainDark}
                alt="Transformative AI"
                className="h-12 w-84 object-contain object-left"
              />
            </a>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white/50 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <p className="text-purple-400 text-sm font-medium tracking-widest uppercase mb-4">About Us</p>
            <h1 className="font-display text-4xl lg:text-5xl text-white mb-6">
              Building Systems That <span className="text-gradient">Scale</span>
            </h1>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Transformative AI designs, builds, and deploys AI-powered operating systems for real businesses—revenue, compliance, decisioning, and human capital.
            </p>
          </div>

          {/* Leadership Section - 4 column grid */}
          <section className="mb-20">
            <p className="text-purple-400 text-sm font-medium tracking-widest uppercase mb-4 text-center">Leadership</p>
            <h2 className="font-display text-3xl text-white mb-12 text-center">Our Team</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {teamMembers.map((member) => (
                <TeamMemberCard key={member.id} member={member} />
              ))}
            </div>
          </section>

          {/* Philosophy */}
          <section className="border-t border-white/5 pt-16">
            <h3 className="font-display text-2xl text-white mb-8 text-center">Our Philosophy</h3>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="p-6 bg-white/[0.02] border border-white/5">
                <p className="text-white/70 leading-relaxed">
                  <span className="text-purple-400 font-medium">Architecture first.</span> We don't sell "AI tools"—we engineer decision systems, revenue systems, compliance systems, and human-capital systems.
                </p>
              </div>
              <div className="p-6 bg-white/[0.02] border border-white/5">
                <p className="text-white/70 leading-relaxed">
                  <span className="text-purple-400 font-medium">Automation second.</span> Our work integrates directly into how organizations operate, making them smarter, faster, and more profitable.
                </p>
              </div>
              <div className="p-6 bg-white/[0.02] border border-white/5">
                <p className="text-white/70 leading-relaxed">
                  <span className="text-purple-400 font-medium">AI last.</span> Technology should earn its keep—every system must justify its existence through measurable outcomes.
                </p>
              </div>
              <div className="p-6 bg-white/[0.02] border border-white/5">
                <p className="text-white/70 leading-relaxed">
                  <span className="text-purple-400 font-medium">Systems over tools.</span> We build durable operating systems that scale without chaos and enforce accountability.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="mt-20 text-center">
            <h3 className="font-display text-2xl text-white mb-4">Ready to Transform Your Operations?</h3>
            <p className="text-white/50 mb-8">Let's discuss how we can build systems that work for your business.</p>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-all"
            >
              Get in Touch
            </a>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-white/30 text-sm">
            © {new Date().getFullYear()} Transformative AI Systems. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
